// Analytics page - detailed analytics for a single link
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  FiArrowLeft, FiExternalLink, FiCopy, FiDownload,
  FiMousePointer, FiTrendingUp, FiClock, FiGlobe,
  FiMonitor, FiSmartphone,
} from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import {
  formatDate,
  remapHoursToLocal,
  formatHour,
  getTimezoneAbbreviation,
} from "../utils/timezone";
import "../styles/Analytics.css";

const COLORS = [
  "#4f46e5", "#059669", "#d97706", "#dc2626",
  "#7c3aed", "#0891b2", "#be185d", "#65a30d",
];

const DEVICE_ICONS = {
  Desktop: <FiMonitor size={14} />,
  Mobile: <FiSmartphone size={14} />,
  Tablet: <FiSmartphone size={14} />,
};

const BASE_DOMAIN = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      <div className="chart-tooltip-value">{payload[0].value.toLocaleString()} clicks</div>
    </div>
  );
}

function BarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{payload[0].payload.name || payload[0].payload.hour}</div>
      <div className="chart-tooltip-value">{payload[0].value.toLocaleString()} clicks</div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { id: linkId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getAnalytics(linkId, period);
      setData(result);
    } catch (err) {
      console.error("Fetch analytics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [linkId, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.exportCSV(linkId);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `linksquire-${data?.short_code || linkId}-analytics.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      addToast("Analytics exported as CSV!", "success");
    } catch (err) {
      addToast("Export failed: " + err.message, "error");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-page loading-state">
        <div className="spinner" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page error-state">
        <p>Error: {error}</p>
        <button className="btn-primary" onClick={() => navigate("/")}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const {
    analytics: {
      by_country = [],
      by_browser = [],
      by_device = [],
      by_os = [],
      by_referrer = [],
      clicks_over_time = {},
      by_hour_of_day = {},
      avg_redirect_time_ms = 0,
    } = {},
  } = data || {};

  const timeSeriesData = Object.entries(clicks_over_time)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({
      date: formatDate(date, "MMM d"),
      clicks: count,
    }));

  // Convert UTC hours to local timezone
  const localHours = remapHoursToLocal(by_hour_of_day);
  const hourData = Array.from({ length: 24 }, (_, i) => ({
    hour: formatHour(i),
    clicks: localHours[i] || 0,
  }));

  const totalDeviceClicks = by_device.reduce((s, d) => s + d.count, 0);

  // Include conversion metrics if available
  const hasConversions = data.total_conversions > 0 || data.conversion_rate > 0;

  const stats = [
    { label: "Total Clicks", value: data.total_clicks || 0, icon: <FiMousePointer />, color: "#4f46e5", sub: "All time" },
    { label: "Period Clicks", value: data.period_clicks || 0, icon: <FiTrendingUp />, color: "#059669", sub: `Last ${period === "7d" ? "7 days" : period === "30d" ? "30 days" : period === "90d" ? "90 days" : "year"}` },
    { label: "Avg Redirect", value: `${avg_redirect_time_ms}ms`, icon: <FiClock />, color: "#d97706", sub: "Response time" },
  ];

  // Add conversion stats if this link has conversions enabled
  if (hasConversions) {
    stats.push({
      label: "Conversions",
      value: data.total_conversions || 0,
      icon: <FiTrendingUp />,
      color: "#7c3aed",
      sub: `${data.conversion_rate || 0}% rate`
    });
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-page-header">
        <div className="analytics-page-header-left">
          <button className="btn-back" onClick={() => navigate("/")}>
            <FiArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="analytics-page-title">{data.title || data.short_code}</h1>
            <div className="analytics-link-meta">
              <span className="analytics-short-link">
                {data.domain || BASE_DOMAIN.replace(/^https?:\/\//, '')}/{data.short_code}
                <button
                  className="btn-icon-sm"
                  onClick={() => {
                    const domain = data.domain ? `https://${data.domain}` : BASE_DOMAIN;
                    navigator.clipboard.writeText(`${domain}/${data.short_code}`);
                    addToast("Copied!", "success");
                  }}
                >
                  <FiCopy size={11} />
                </button>
              </span>
              <a
                href={data.original_url}
                target="_blank"
                rel="noopener noreferrer"
                className="analytics-dest-link"
              >
                {data.original_url} <FiExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
        <div className="analytics-page-header-right">
          <div className="period-selector">
            {["7d", "30d", "90d", "365d"].map((p) => (
              <button
                key={p}
                className={period === p ? "active" : ""}
                onClick={() => setPeriod(p)}
              >
                {p === "7d" ? "7D" : p === "30d" ? "30D" : p === "90d" ? "90D" : "1Y"}
              </button>
            ))}
          </div>
          <button
            className="btn-secondary export-btn"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? (
              <><span className="spinner-sm dark" /> Exporting...</>
            ) : (
              <><FiDownload size={13} /> Export</>
            )}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="analytics-stat-cards">
        {stats.map((s) => (
          <div key={s.label} className="analytics-stat-card">
            <div className="analytics-stat-icon" style={{ background: `${s.color}12`, color: s.color }}>
              {s.icon}
            </div>
            <div className="analytics-stat-content">
              <span className="analytics-stat-value">{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</span>
              <span className="analytics-stat-label">{s.label}</span>
            </div>
            <span className="analytics-stat-sub">{s.sub}</span>
          </div>
        ))}
      </div>

      {/* Clicks over time - area chart */}
      <div className="analytics-card analytics-card-wide">
        <div className="analytics-card-header">
          <h3>Clicks Over Time</h3>
          <span className="analytics-card-badge">{data.period_clicks?.toLocaleString()} clicks</span>
        </div>
        {timeSeriesData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timeSeriesData}>
              <defs>
                <linearGradient id="colorClicksLink" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="clicks" stroke="#4f46e5" strokeWidth={2} fill="url(#colorClicksLink)" dot={false} activeDot={{ r: 4, fill: "#4f46e5" }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty-state">
            <FiMousePointer size={24} />
            <p>No click data for this period</p>
          </div>
        )}
      </div>

      {/* Two column grid */}
      <div className="analytics-grid-2">
        {/* Countries */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3><FiGlobe size={14} /> Top Countries</h3>
          </div>
          {by_country.length > 0 ? (
            <div className="analytics-list">
              {by_country.slice(0, 8).map((c, i) => {
                const pct = data.period_clicks > 0 ? ((c.count / data.period_clicks) * 100).toFixed(1) : 0;
                return (
                  <div key={c.name} className="analytics-list-item">
                    <span className="analytics-list-rank">{i + 1}</span>
                    <span className="analytics-list-name">{c.name}</span>
                    <div className="analytics-list-bar-track">
                      <div className="analytics-list-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="analytics-list-count">{c.count.toLocaleString()}</span>
                    <span className="analytics-list-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="chart-empty-state small">
              <FiGlobe size={20} />
              <p>No location data yet</p>
            </div>
          )}
        </div>

        {/* Browsers */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Browsers</h3>
          </div>
          {by_browser.length > 0 ? (
            <div className="analytics-donut-container">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={by_browser.slice(0, 6)}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {by_browser.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<BarTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="analytics-legend">
                {by_browser.slice(0, 6).map((b, i) => (
                  <div key={b.name} className="analytics-legend-item">
                    <span className="analytics-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="analytics-legend-label">{b.name}</span>
                    <span className="analytics-legend-value">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="chart-empty-state small">
              <p>No browser data yet</p>
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Devices</h3>
          </div>
          {by_device.length > 0 ? (
            <div className="analytics-device-grid">
              {by_device.map((d, i) => {
                const pct = totalDeviceClicks > 0 ? ((d.count / totalDeviceClicks) * 100).toFixed(0) : 0;
                return (
                  <div key={d.name} className="analytics-device-card">
                    <div className="analytics-device-icon" style={{ color: COLORS[i % COLORS.length] }}>
                      {DEVICE_ICONS[d.name] || <FiMonitor size={14} />}
                    </div>
                    <div className="analytics-device-info">
                      <span className="analytics-device-name">{d.name}</span>
                      <span className="analytics-device-pct">{pct}%</span>
                    </div>
                    <div className="analytics-device-bar-track">
                      <div className="analytics-device-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="analytics-device-count">{d.count.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="chart-empty-state small">
              <p>No device data yet</p>
            </div>
          )}
        </div>

        {/* OS */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Operating Systems</h3>
          </div>
          {by_os.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={by_os.slice(0, 6)} layout="vertical" barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: "#37352f" }} axisLine={false} tickLine={false} />
                <Tooltip content={<BarTooltip />} />
                <Bar dataKey="count" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty-state small">
              <p>No OS data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Clicks by hour */}
      <div className="analytics-card analytics-card-wide">
        <div className="analytics-card-header">
          <h3>Clicks by Hour <span className="tz-indicator">({getTimezoneAbbreviation()})</span></h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hourData} barSize={12}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#9b9a97" }} axisLine={false} tickLine={false} interval={2} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} width={30} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="clicks" fill="#d97706" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Referrers */}
      <div className="analytics-card analytics-card-wide">
        <div className="analytics-card-header">
          <h3>Top Referrers</h3>
        </div>
        {by_referrer.length > 0 ? (
          <div className="analytics-list">
            {by_referrer.slice(0, 10).map((r, i) => {
              const pct = data.period_clicks > 0 ? ((r.count / data.period_clicks) * 100).toFixed(1) : 0;
              return (
                <div key={r.name} className="analytics-list-item">
                  <span className="analytics-list-rank">{i + 1}</span>
                  <span className="analytics-list-name">{r.name}</span>
                  <div className="analytics-list-bar-track">
                    <div className="analytics-list-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                  <span className="analytics-list-count">{r.count.toLocaleString()}</span>
                  <span className="analytics-list-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="chart-empty-state small">
            <p>No referrer data yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
